import { useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  useInput,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { TextInput } from "@/components/admin/text-input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { FormSection } from "../layout/FormLayout";
import type { CrmDataProvider } from "../providers/types";
import type { Contact } from "../types";

// Samtykke til markedsføring (kap. 6.8): avkrysning, kilde og den nøyaktige
// teksten kontakten godtok. consent_at settes når boksen krysses av, og alt
// tømmes når den krysses ut.
export const ContactConsentInputs = () => {
  const translate = useTranslate();
  const { setValue } = useFormContext();
  const { field } = useInput({ source: "marketing_consent" });
  const consent = !!useWatch({ name: "marketing_consent" });
  const consentAt = useWatch({ name: "consent_at" }) as string | null;
  const [locale = "nb"] = useLocaleState();
  const previous = useRef(consent);

  useEffect(() => {
    if (consent === previous.current) return;
    previous.current = consent;
    if (consent) {
      setValue("consent_at", new Date().toISOString(), { shouldDirty: true });
    } else {
      for (const source of ["consent_at", "consent_source", "consent_text"])
        setValue(source, null, { shouldDirty: true });
    }
  }, [consent, setValue]);

  return (
    <FormSection
      title={translate("crm.contacts.consent.title", { _: "Samtykke" })}
    >
      <div className="flex items-center gap-2">
        <Checkbox
          id="contact-marketing-consent"
          checked={consent}
          onCheckedChange={(checked) => field.onChange(checked === true)}
        />
        <Label htmlFor="contact-marketing-consent" className="font-normal">
          {translate("crm.contacts.consent.marketing", {
            _: "Samtykke til markedsføring",
          })}
        </Label>
      </div>
      {consent && (
        <>
          {consentAt && (
            <p className="text-xs text-muted-foreground">
              {translate("crm.contacts.consent.given_at", {
                _: "Gitt %{date}",
                date: new Date(consentAt).toLocaleDateString(locale),
              })}
            </p>
          )}
          <TextInput
            source="consent_source"
            label={translate("crm.contacts.consent.source", { _: "Kilde" })}
            helperText={translate("crm.contacts.consent.source_help", {
              _: "For eksempel «Nettskjema: /kontakt».",
            })}
          />
          <TextInput
            source="consent_text"
            multiline
            label={translate("crm.contacts.consent.text", {
              _: "Samtykketekst",
            })}
            helperText={translate("crm.contacts.consent.text_help", {
              _: "Den nøyaktige teksten kontakten godtok.",
            })}
          />
        </>
      )}
    </FormSection>
  );
};

// Avmeldte adresser (lowercase). Mangler registeret (demo), regnes ingen som
// avmeldt.
const useOptouts = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: ["email_optouts"],
    queryFn: async () => {
      try {
        const emails = await dataProvider.getEmailOptouts();
        return new Set(emails.map((e) => e.trim().toLowerCase()));
      } catch {
        return new Set<string>();
      }
    },
    staleTime: 60_000,
  });
};

// Samtykkestatus på kontaktkortet. En avmelding går alltid foran samtykket.
export const ContactConsentInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const [locale = "nb"] = useLocaleState();
  const { data: optouts } = useOptouts();
  if (!record) return null;

  const optedOut = (record.email_jsonb ?? []).some(
    (e) => !!e.email && optouts?.has(e.email.trim().toLowerCase()),
  );

  if (optedOut)
    return (
      <p className="text-sm">
        <span className="font-medium text-destructive">
          {translate("crm.contacts.consent.opted_out", { _: "Avmeldt" })}
        </span>
        <span className="block text-xs text-muted-foreground">
          {translate("crm.contacts.consent.opted_out_help", {
            _: "Avmeldingen går foran samtykket. Kontakten får ingen markedsføring.",
          })}
        </span>
      </p>
    );

  if (!record.marketing_consent)
    return (
      <p className="text-sm text-muted-foreground">
        {translate("crm.contacts.consent.none", {
          _: "Ikke samtykke til markedsføring",
        })}
      </p>
    );

  const details = [
    record.consent_at
      ? new Date(record.consent_at).toLocaleDateString(locale)
      : null,
    record.consent_source,
  ].filter(Boolean);

  return (
    <div className="text-sm">
      <p>
        {translate("crm.contacts.consent.given", {
          _: "Samtykke til markedsføring",
        })}
        {details.length ? (
          <span className="text-muted-foreground"> ({details.join(", ")})</span>
        ) : null}
      </p>
      {record.consent_text && (
        <p className="mt-1 text-xs text-muted-foreground italic">
          «{record.consent_text}»
        </p>
      )}
    </div>
  );
};
